/* eslint-disable @typescript-eslint/no-unsafe-return */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import { Component, Inject, OnDestroy, OnInit } from "@angular/core";
import { CommonModule } from "@angular/common";

// PrimeNG imports
import { DialogModule } from "primeng/dialog";
import { ButtonModule } from "primeng/button";
import { SkeletonModule } from "primeng/skeleton";
import { TagModule } from "primeng/tag";
import { DividerModule } from "primeng/divider";
import { TooltipModule } from "primeng/tooltip";
import { CardModule } from "primeng/card";
import { MessageModule } from "primeng/message";
import { DynamicDialogRef, DynamicDialogConfig } from "primeng/dynamicdialog";

// Our services and models
import {
  QPilotService,
  QPilotAccessToken,
} from "../../../../core/services/qpilot.service";
import { SessionService } from "../../../../core/services/session.service";
import { SiteFull } from "../../../../core/models/site.interface";
import { TimezoneHelper } from "../../../../core/utils/timezone.helper";
import { ProcessingErrorCodesHelper } from "../../../settings/utils/processing-error-codes.helper";
import { ProcessingErrorCode } from "../../../settings/interfaces/email-settings.interface";
import {
  ScheduledOrderListItem,
  ValidationResult,
} from "../../interfaces/scheduled-orders-list-item.interface";
import { Subject, takeUntil } from "rxjs";

interface ValidationCheckItem {
  processingErrorCode: number;
  successText: string;
  errorText: string;
  additionalErrorText?: string;
  isCheckFailed: boolean;
  isCheckSkipped: boolean;
  isCheckWarning: boolean;
  isCheckShippingRateWarning: boolean;
}

/**
 * Validation Checks Dialog Component
 * Matches the old QMC ValidationChecksPopupViewComponent functionality
 */
@Component({
  selector: "app-validation-checks-dialog",
  standalone: true,
  imports: [
    CommonModule,
    DialogModule,
    ButtonModule,
    SkeletonModule,
    TagModule,
    DividerModule,
    TooltipModule,
    CardModule,
    MessageModule,
  ],
  templateUrl: "./validation-checks-dialog.component.html",
  styleUrl: "./validation-checks-dialog.component.scss",
})
export class ValidationChecksDialogComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();

  scheduledOrder!: ScheduledOrderListItem;
  site!: SiteFull;
  dontPersistPreValidationRefresh = false;

  isLoading = false;
  isRefreshing = false;
  validationChecks: ValidationCheckItem[] = [];
  lastUpdated?: Date;
  overallStatus: "success" | "warning" | "error" | "pending" = "pending";
  shippingWarnings: string[] = [];
  showingDetailsForCheck: number | null = null;
  additionalErrorText = "";

  constructor(
    @Inject(DynamicDialogConfig) public config: DynamicDialogConfig,
    private ref: DynamicDialogRef,
    private qpilotService: QPilotService,
    private sessionService: SessionService,
    private timezoneHelper: TimezoneHelper,
    private processingErrorCodesHelper: ProcessingErrorCodesHelper,
  ) {
    if (config.data) {
      this.scheduledOrder = config.data.scheduledOrder;
      this.site = config.data.site;
      this.dontPersistPreValidationRefresh =
        config.data.dontPersistPreValidationRefresh || false;
    }
  }

  ngOnInit(): void {
    this.initializeValidationChecks();

    // Auto-refresh validation checks if data is missing
    // This matches old QMC behavior where validation is always shown
    if (!this.scheduledOrder.ValidationChecks) {
      // Automatically refresh to get validation data
      this.onRefreshValidation();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Initialize validation checks display based on current validation result
   */
  private initializeValidationChecks(): void {
    this.validationChecks = this.buildValidationChecksList();
    this.updateOverallStatus();
    this.extractShippingWarnings();

    if (this.scheduledOrder.ValidationChecks?.UpdatedUtc) {
      this.lastUpdated = new Date(
        this.scheduledOrder.ValidationChecks.UpdatedUtc,
      );
    }
  }

  /**
   * Build validation checks list based on old QMC validation structure
   * Using the exact error codes and logic from the old implementation
   */
  private buildValidationChecksList(): ValidationCheckItem[] {
    const checks: ValidationCheckItem[] = [];

    // If no validation result exists at all, return empty array
    // This will show "No validation checks have been run" message
    if (!this.scheduledOrder.ValidationChecks) {
      return checks;
    }

    // Extract shipping rate error additional text if present
    let shippingRateErrorAdditionalText: string | undefined = undefined;
    if (this.scheduledOrder.ValidationChecks.ErrorItems) {
      const errors = this.scheduledOrder.ValidationChecks.ErrorItems;
      const shippingRateError = errors.find(
        (e) => e.ProcessingErrorCode === 1002,
      );

      if (shippingRateError && shippingRateError.ScheduledOrderFailureReason) {
        shippingRateErrorAdditionalText =
          shippingRateError.ScheduledOrderFailureReason;
      }
    }

    // Validation failed but no error items - return empty
    if (
      !this.scheduledOrder.ValidationChecks.Succeeded &&
      !this.scheduledOrder.ValidationChecks.ErrorItems
    ) {
      return checks;
    }

    // Build the validation check items in the same order as old QMC
    // 1004: Payment Method
    checks.push(
      this.createValidationCheckItem(
        1004,
        "Payment Method set",
        "No Payment Method exists",
      ),
    );

    // 1003: Payment Integration
    checks.push(
      this.createValidationCheckItem(
        1003,
        "Payment Integration set",
        "No Payment Integration exists",
      ),
    );

    // 1000: Contains items
    checks.push(
      this.createValidationCheckItem(
        1000,
        "Contains items",
        "Scheduled Order is Empty",
      ),
    );

    // 1001: Items are valid
    checks.push(
      this.createValidationCheckItem(
        1001,
        "Items are valid",
        "Items are invalid",
      ),
    );

    // 1002: Shipping Rate
    checks.push(
      this.createValidationCheckItem(
        1002,
        "Shipping Rate set",
        "No Shipping Rate found",
        shippingRateErrorAdditionalText,
      ),
    );

    // Set failed status for each check
    checks.forEach((check) => {
      check.isCheckFailed = this.isCheckFailed(check.processingErrorCode);
    });

    // Apply skip logic (based on old QMC logic)
    // If no items (1000 failed), skip item validity (1001) and shipping rate (1002)
    if (checks[2].isCheckFailed) {
      checks[3].isCheckSkipped = true;
      checks[4].isCheckSkipped = true;
    }

    // If items are invalid (1001 failed), skip shipping rate (1002)
    if (checks[3].isCheckFailed) {
      checks[4].isCheckSkipped = true;
    }

    // If no payment method (1004 failed), skip payment integration (1003)
    if (checks[0].isCheckFailed) {
      checks[1].isCheckSkipped = true;
    }

    // Check for shipping rate calculation warnings
    if (this.hasShippingRateCalculationErrors(checks[4])) {
      checks[4].isCheckWarning = true;
      checks[4].isCheckShippingRateWarning = true;
    }

    return checks;
  }

  /**
   * Create a validation check item
   */
  private createValidationCheckItem(
    processingErrorCode: number,
    successText: string,
    errorText: string,
    additionalErrorText?: string,
  ): ValidationCheckItem {
    return {
      processingErrorCode,
      successText,
      errorText,
      additionalErrorText,
      isCheckFailed: false,
      isCheckSkipped: false,
      isCheckWarning: false,
      isCheckShippingRateWarning: false,
    };
  }

  /**
   * Check if a validation check failed based on error code
   */
  private isCheckFailed(processingErrorCode: number): boolean {
    if (this.scheduledOrder.ValidationChecks) {
      const errors = this.scheduledOrder.ValidationChecks.ErrorItems;
      if (errors) {
        return (
          errors.find((e) => e.ProcessingErrorCode === processingErrorCode) !==
          undefined
        );
      }
    }
    return false;
  }

  /**
   * Check if there are shipping rate calculation errors
   */
  private hasShippingRateCalculationErrors(
    validationCheckItem: ValidationCheckItem,
  ): boolean {
    return (
      validationCheckItem.processingErrorCode === 1002 &&
      !!this.scheduledOrder.Metadata?.ShippingRateCalculationErrors &&
      this.scheduledOrder.Metadata.ShippingRateCalculationErrors.length > 0
    );
  }

  /**
   * Update overall status based on individual checks
   */
  private updateOverallStatus(): void {
    const hasFailedChecks = this.validationChecks.some(
      (check) => check.isCheckFailed && !check.isCheckSkipped,
    );
    const hasWarnings = this.validationChecks.some(
      (check) => check.isCheckWarning,
    );

    if (hasFailedChecks) {
      this.overallStatus = "error";
    } else if (hasWarnings) {
      this.overallStatus = "warning";
    } else if (this.scheduledOrder.ValidationChecks?.Succeeded) {
      this.overallStatus = "success";
    } else {
      this.overallStatus = "pending";
    }
  }

  /**
   * Extract shipping warnings from metadata
   */
  private extractShippingWarnings(): void {
    this.shippingWarnings = [];

    if (this.scheduledOrder.Metadata?.ShippingRateCalculationErrors) {
      this.shippingWarnings =
        this.scheduledOrder.Metadata.ShippingRateCalculationErrors;
    }
  }

  /**
   * Get access token with fallback to session service
   * Supports embedded mode where token is passed via dialog config
   */
  private getAccessToken(): QPilotAccessToken | undefined {
    return this.config.data?.accessToken ?? this.sessionService.accessToken;
  }

  /**
   * Refresh validation results
   * Uses getScheduledOrderPreValidationResult if dontPersistPreValidationRefresh is true,
   * otherwise uses updateScheduledOrderPreValidationResult to persist to database
   */
  onRefreshValidation(): void {
    this.isRefreshing = true;
    const accessToken = this.getAccessToken();

    if (!accessToken) {
      this.isRefreshing = false;
      return;
    }

    // Choose the appropriate API method based on dontPersistPreValidationRefresh flag
    // OLD QMC: validation-checks-popup-view.component.ts lines 48-66
    const validationObservable = this.dontPersistPreValidationRefresh
      ? this.qpilotService.getScheduledOrderPreValidationResult(
          this.site.Id,
          this.scheduledOrder, // Pass full order object (matches old QMC)
          accessToken,
        )
      : this.qpilotService.updateScheduledOrderPreValidationResult(
          this.site.Id,
          this.scheduledOrder.Id, // This one takes just ID
          accessToken,
        );

    validationObservable.pipe(takeUntil(this.destroy$)).subscribe({
      next: (result: ValidationResult) => {
        this.scheduledOrder.ValidationChecks = result;
        this.initializeValidationChecks();
        this.isRefreshing = false;
      },
      error: (error: unknown) => {
        console.error("Error refreshing validation:", error);
        this.isRefreshing = false;
      },
    });
  }

  /**
   * Get formatted last updated time
   */
  getFormattedLastUpdated(): string {
    if (!this.lastUpdated) return "Never";
    const utcOffset = this.site?.UtcOffset ?? 0;
    return this.timezoneHelper.formatDateWithTimezone(
      this.lastUpdated,
      utcOffset,
    );
  }

  /**
   * Get status icon for validation check
   */
  getStatusIcon(check: ValidationCheckItem): string {
    if (check.isCheckSkipped) {
      return "pi pi-minus-circle text-500";
    } else if (check.isCheckFailed) {
      return "pi pi-times-circle text-red-600";
    } else if (check.isCheckWarning) {
      return "pi pi-exclamation-triangle text-yellow-600";
    } else {
      return "pi pi-check-circle text-green-600";
    }
  }

  /**
   * Get status severity for PrimeNG components
   */
  getStatusSeverity(
    status: "success" | "warning" | "error" | "pending",
  ): "success" | "warning" | "danger" | "info" {
    switch (status) {
      case "success":
        return "success";
      case "error":
        return "danger";
      case "warning":
        return "warning";
      case "pending":
      default:
        return "info";
    }
  }

  /**
   * Get status text for validation check
   */
  getStatusText(check: ValidationCheckItem): string {
    if (check.isCheckFailed) {
      return check.errorText;
    } else {
      return check.successText;
    }
  }

  /**
   * Get CSS class for check status
   */
  getCheckStatusClass(check: ValidationCheckItem): string {
    if (check.isCheckSkipped) {
      return "text-500";
    } else if (check.isCheckFailed) {
      return "text-red-500";
    } else if (check.isCheckWarning) {
      return "text-yellow-500";
    } else {
      return "text-green-500";
    }
  }

  /**
   * Toggle showing details for a specific check
   */
  toggleDetails(check: ValidationCheckItem): void {
    if (this.showingDetailsForCheck === check.processingErrorCode) {
      this.showingDetailsForCheck = null;
      this.additionalErrorText = "";
    } else {
      this.showingDetailsForCheck = check.processingErrorCode;
      this.additionalErrorText = check.additionalErrorText || "";
    }
  }

  /**
   * Check if details are showing for a specific check
   */
  isShowingDetails(check: ValidationCheckItem): boolean {
    return this.showingDetailsForCheck === check.processingErrorCode;
  }

  /**
   * Get error code documentation URL
   */
  getErrorCodeUrl(errorCode: number): string | null {
    return this.processingErrorCodesHelper.getDocumentationUrlFromProcessingErrorCode(
      errorCode as ProcessingErrorCode,
    );
  }

  /**
   * Close dialog
   */
  onClose(): void {
    this.ref.close(this.scheduledOrder);
  }

  /**
   * Track by function for validation checks ngFor performance
   */
  trackByCheckName(index: number, check: ValidationCheckItem): number {
    return check.processingErrorCode;
  }
}
